/**
 * Editorial verdict for a city-distance guess. Maps a kilometer
 * distance to a short human-readable phrase the result panel renders
 * next to the raw distance — gives the moment a tiny bit of personality
 * without forcing the player to mentally rank "is 230 km good or bad?"
 *
 * Bands lifted from the reference prototype's mile thresholds and
 * converted to round km values so the math is unit-agnostic internally
 * (the SPA already converts to miles for display via formatDistance).
 *
 *   < 40 km    Pinpoint range          (~25 mi)
 *   < 120 km   Bullseye range          (~75 mi)
 *   < 320 km   Very close              (~200 mi)
 *   < 800 km   Right neighborhood      (~500 mi)
 *   < 1600 km  In the region           (~1000 mi)
 *   < 3200 km  Right continent at least (~2000 mi)
 *   < 6400 km  Wrong continent         (~4000 mi)
 *   else       A world away
 *
 * Each band has multiple alternative phrases — a fresh phrase rolls
 * each time the function is called, so the UI feels less repetitive
 * across rounds. Callers should call `verdictForKm` ONCE per click and
 * stash the result in component state; calling it inside a `$derived`
 * would reroll the phrase on every re-render.
 *
 * The optional `rng` parameter exists for tests — pass a stable rng
 * (e.g. `() => 0`) to assert the function picks the right band's first
 * phrase without depending on Math.random.
 */

const PINPOINT_PHRASES = [
  'Pinpoint. Extraordinary.',
  'Dead on.',
  'Surgical strike.',
  'Bullseye, basically.',
  "You'd find it blindfolded.",
  'On the nose.',
  'Razor sharp.',
  'A jeweler would be jealous.',
  'Cartographer-grade aim.',
  'No notes.',
  'Eerily exact.',
  'Practically the city limits.',
  'Hand-on-the-pin precision.',
  'Walked right to it.',
  'A guidebook would round to your guess.',
  'Pixel-perfect.',
  'You could mail a letter from there.',
  "It's like you live there.",
  'Geo-savant move.',
];

const BULLSEYE_PHRASES = [
  'Bullseye.',
  'Knife throw close.',
  'Same county, basically.',
  'Strong instincts.',
  'Sharp work.',
  'Dialed in.',
  'A friendly drive away.',
  'You know the area.',
  "That's a confident click.",
  'Edge of the metro.',
  'Nice locking-in.',
  'Suburb-level accuracy.',
  'Within a road trip.',
  'Crisp answer.',
  'Tight grouping.',
  'Map-savvy.',
  'Within radio range.',
  'Same time zone, easily.',
  'A short flight at most.',
];

const VERY_CLOSE_PHRASES = [
  'Very close.',
  'Same general area.',
  'In the right neighborhood of the country.',
  "You're hot.",
  'Within striking distance.',
  'Same region, no doubt.',
  'Same weather, probably.',
  'Train ride away.',
  'Honest mistake territory.',
  'Sniffing it out.',
  'Same road network.',
  'A long drive at worst.',
  "You've been to one of these.",
  'Roughly right.',
  'Local-ish.',
  'Within a watershed.',
  'A few cities off.',
  'Could be worse — much worse.',
  'Solid attempt.',
];

const RIGHT_NEIGHBORHOOD_PHRASES = [
  'Right neighborhood.',
  'Same broader region.',
  'Within day-trip distance.',
  'Same chunk of the map.',
  "Country's general direction.",
  'A bus ride and a nap.',
  'You had the right idea.',
  'Reasonable guess.',
  'Within the same dialect, probably.',
  'Same broader culture.',
  "Half the country's correct.",
  'A respectable margin.',
  'Could pass for a near miss.',
  'In the ballpark, generously.',
  'Local on a continent scale.',
  'A morning drive away.',
  "You're warm.",
  'Within an overnight train.',
  'Same broad climate.',
];

const IN_THE_REGION_PHRASES = [
  'In the region.',
  'Sub-continent right.',
  'Same broader zone.',
  'Same flag, often.',
  'Big country, but right one.',
  'Same time zone, probably.',
  "Within a country's worth.",
  'Same political block.',
  'You knew the half of the world.',
  'Reasonable instinct.',
  'A day on the road.',
  'Continental neighbors.',
  'Same airline hub.',
  'Same currency, often.',
  'Same broad ecosystem.',
  'Within the same large nation.',
  'You smelled the right area.',
  'Roughly oriented.',
  'Geographic ballpark.',
];

const RIGHT_CONTINENT_PHRASES = [
  'Right continent at least.',
  'Right landmass, anyway.',
  'Same continent, different story.',
  'Fly across, drive across.',
  "You're in the right hemisphere.",
  'Continental aim.',
  'Right tectonic plate.',
  'You knew the broad sweep.',
  'Same coastline, eventually.',
  'Half-credit.',
  'Big map, right corner.',
  'Same general empire, historically.',
  'Within a multi-day train.',
  'Same airspace.',
  'Right side of the ocean.',
  'You waved at the country, at least.',
  'Same atlas page, probably.',
  'Plausible miss.',
  "You're not lost — just far.",
];

const WRONG_CONTINENT_PHRASES = [
  'Wrong continent.',
  "That's an ocean off.",
  'Different landmass entirely.',
  'Time zone tornado.',
  "You're swinging big.",
  'Different hemisphere.',
  'Long-haul-flight wrong.',
  'Continents apart.',
  'Sea-and-then-some away.',
  'Different climate zone.',
  "We're in different stories.",
  'Two stamps in the passport away.',
  'Whole flag away.',
  'Other side of the equator.',
  'Big swing, big miss.',
  'Different tectonic plate.',
  'Far field.',
  'Different hemisphere of culture.',
  'A whole sea between you.',
];

const WORLD_AWAY_PHRASES = [
  'A world away.',
  'Almost the antipode.',
  "Couldn't be farther.",
  'Other side of the planet.',
  'Halfway around the globe.',
  'Geographically opposite.',
  'About as wrong as wrong gets.',
  'Antipodean, almost.',
  'Different ocean, different luck.',
  'Through the Earth would be quicker.',
  'Polar miss.',
  'Wrong side of the planet entirely.',
  'You went sightseeing.',
  'Maximum geography.',
  'A flight, a flight, a long drive.',
  'A literal world apart.',
  'Globe span miss.',
  "We're in different worlds.",
  'Catastrophic geography.',
];

interface VerdictBand {
  /** Upper bound (km, exclusive). The last band uses Infinity. */
  max: number;
  phrases: readonly string[];
}

const BANDS: readonly VerdictBand[] = [
  { max: 40, phrases: PINPOINT_PHRASES },
  { max: 120, phrases: BULLSEYE_PHRASES },
  { max: 320, phrases: VERY_CLOSE_PHRASES },
  { max: 800, phrases: RIGHT_NEIGHBORHOOD_PHRASES },
  { max: 1600, phrases: IN_THE_REGION_PHRASES },
  { max: 3200, phrases: RIGHT_CONTINENT_PHRASES },
  { max: 6400, phrases: WRONG_CONTINENT_PHRASES },
  { max: Infinity, phrases: WORLD_AWAY_PHRASES },
];

export function verdictForKm(km: number, rng: () => number = Math.random): string {
  for (const band of BANDS) {
    if (km < band.max) {
      const idx = Math.floor(rng() * band.phrases.length);
      return band.phrases[Math.min(idx, band.phrases.length - 1)];
    }
  }
  // Unreachable — last band's max is Infinity. Defensive fallback.
  const last = BANDS[BANDS.length - 1].phrases;
  return last[0];
}

/**
 * Test helper — returns the full phrase array for the band that `km`
 * falls into. Lets callers assert "the result is one of the right
 * band's phrases" without re-implementing band logic in tests.
 */
export function _phrasesForKm(km: number): readonly string[] {
  for (const band of BANDS) {
    if (km < band.max) return band.phrases;
  }
  return BANDS[BANDS.length - 1].phrases;
}
